import { useContext, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { callAPI, unsafeAPI } from '../../hooks/api';
import { Search, CheckCircle, XCircle, Clock, AlertTriangle, KeyRound, Copy, Loader2, ShieldOff } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Dialog } from '../../components/Dialog';
import Textarea from '../../components/Textarea';
import { Button } from '../../shadcn/Button';

interface DeviceResult {
	success?: boolean;
	status?: string;
	device_imei_no?: string;
	enroll_period?: boolean;
	start_date?: string;
}

interface ReleaseState {
	teardown_status: 'erase_sent' | 'completed';
	bypass_code: string | null;
}

export default function BuscarIphones() {
	const { userData } = useContext(GlobalContext);

	const [imeiInput, setImeiInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{
		title: string;
		message: string;
		type?: 'success' | 'error' | 'warning';
	} | null>(null);
	const [results, setResults] = useState<DeviceResult[]>([]);
	const [hasSearched, setHasSearched] = useState(false);
	const [releaseImei, setReleaseImei] = useState<string | null>(null);
	const [releaseStates, setReleaseStates] = useState<Record<string, ReleaseState>>({});
	const [bypassCodes, setBypassCodes] = useState<Record<string, { loading: boolean; code: string | null; error: string | null }>>(
		{}
	);

	const parseIMEIs = (input: string): string[] => {
		return Array.from(
			new Set(
				input
					.split(/[\n,;]+/)
					.map((id) => id.trim())
					.filter((id) => id.length > 0)
			)
		);
	};

	const parsedIMEIs = parseIMEIs(imeiInput);

	const calculateDaysLeft = (startDate: string): number => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const enrollDate = new Date(startDate + 'T00:00:00');
		const diffTime = enrollDate.getTime() - today.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr + 'T00:00:00');
		return date.toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const handleSearch = async () => {
		const imeis = parsedIMEIs;

		if (imeis.length === 0) {
			setAlertDialog({
				title: 'Error',
				message: 'Por favor ingrese al menos un IMEI para buscar',
				type: 'warning'
			});
			return;
		}

		setLoading(true);
		setResults([]);
		setHasSearched(true);

		try {
			const result = await unsafeAPI(
				'POST',
				'https://api.krece.app/administration/kandjicheckenrollperiod/',
				{ devices: imeis.map((imei) => ({ device_imei_no: imei })) },
				userData,
				null
			);

			if (!result || !result.success) {
				let errorMessage = result?.status || 'Ha ocurrido un error al buscar los dispositivos';
				if (result?.status === 'Datos mal formados') {
					errorMessage = imeis.length > 1 ? 'IMEIs inválidos o no encontrados' : 'IMEI inválido o no encontrado';
				}
				setAlertDialog({
					title: 'Error',
					message: errorMessage,
					type: 'error'
				});
				setResults([]);
				return;
			}

			if (!result.data || result.data.length === 0) {
				setAlertDialog({
					title: 'Sin resultados',
					message: 'No se encontraron dispositivos con los IMEIs proporcionados',
					type: 'warning'
				});
				setResults([]);
				return;
			}

			setResults(result.data);
		} catch (error) {
			setAlertDialog({
				title: 'Error',
				message: 'Ha ocurrido un error inesperado al buscar los dispositivos',
				type: 'error'
			});
			console.error('Error searching devices:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleGetBypassCode = async (imei: string) => {
		setBypassCodes((prev) => ({ ...prev, [imei]: { loading: true, code: null, error: null } }));
		try {
			const result = await unsafeAPI(
				'POST',
				'https://api.krece.app/administration/kandjibypasscode/',
				{ device_imei_no: imei },
				userData,
				null
			);
			if (result?.success && result.data) {
				setBypassCodes((prev) => ({ ...prev, [imei]: { loading: false, code: result.data, error: null } }));
			} else {
				setBypassCodes((prev) => ({
					...prev,
					[imei]: { loading: false, code: null, error: result?.status || 'No se pudo obtener el código' }
				}));
			}
		} catch {
			setBypassCodes((prev) => ({
				...prev,
				[imei]: { loading: false, code: null, error: 'Error al obtener el código de bypass' }
			}));
		}
	};

	const handleRelease = async (imei: string): Promise<true | string> => {
		const [response, error] = await callAPI(
			'https://api.krece.app/administration/teardownappledevice/',
			'POST',
			{ device_imei_no: imei, confirm_erase: true },
			userData
		);

		if (error) return error;
		if (!response) return 'No se pudo procesar la liberación. Intente nuevamente.';

		setReleaseStates((previous) => ({ ...previous, [imei]: response }));
		return true;
	};

	const getDeviceStatus = (device: DeviceResult) => {
		if (device.success === false) {
			return {
				type: 'not_found' as const,
				icon: <XCircle className="h-4 w-4 text-neutral-500" />,
				label: 'No encontrado',
				description: 'Este iPhone no está registrado en Krece'
			};
		}

		if (device.enroll_period === true) {
			return {
				type: 'enrolled' as const,
				icon: <CheckCircle className="h-4 w-4 text-green-500" />,
				label: 'Listo para la venta',
				description: 'Este iPhone está listo para ser vendido'
			};
		}

		if (device.enroll_period === false && device.start_date) {
			const daysLeft = calculateDaysLeft(device.start_date);
			const formattedDate = formatDate(device.start_date);
			return {
				type: 'pending' as const,
				icon: <Clock className="h-4 w-4 text-yellow-500" />,
				label: 'Pendiente',
				description: `Estará listo el ${formattedDate}`,
				daysLeft: daysLeft > 0 ? daysLeft : 0
			};
		}

		return {
			type: 'unknown' as const,
			icon: <AlertTriangle className="h-4 w-4 text-orange-500" />,
			label: 'Estado desconocido',
			description: device.status || 'No se pudo determinar el estado del dispositivo'
		};
	};

	const enrolledCount = results.filter(
		(result) => result.enroll_period === true && (!result.device_imei_no || !releaseStates[result.device_imei_no])
	).length;
	const releasePendingCount = results.filter(
		(result) => result.device_imei_no && releaseStates[result.device_imei_no]?.teardown_status === 'erase_sent'
	).length;
	const releasedCount = results.filter(
		(result) => result.device_imei_no && releaseStates[result.device_imei_no]?.teardown_status === 'completed'
	).length;
	const pendingCount = results.filter((r) => r.enroll_period === false && r.start_date).length;
	const notFoundCount = results.filter((r) => r.success === false).length;

	const getStatusPriority = (device: DeviceResult): number => {
		if (device.enroll_period === true) return 0;
		if (device.enroll_period === false && device.start_date) return 1;
		return 2;
	};

	const sortedResults = [...results].sort((a, b) => getStatusPriority(a) - getStatusPriority(b));

	return (
		<div className="mx-auto w-full max-w-2xl p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<div className="flex items-start gap-3">
						{alertDialog.type === 'success' && <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />}
						{alertDialog.type === 'error' && <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />}
						{alertDialog.type === 'warning' && <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />}
						<p className="text-neutral-200">{alertDialog.message}</p>
					</div>
				</Dialog>
			)}

			{releaseImei && (
				<ConfirmDialog
					title={releaseStates[releaseImei]?.teardown_status === 'erase_sent' ? 'Verificar liberación' : 'Liberar iPhone'}
					confirmVariant={releaseStates[releaseImei]?.teardown_status === 'erase_sent' ? 'primary' : 'destructive'}
					acknowledgement={
						releaseStates[releaseImei]?.teardown_status === 'erase_sent'
							? undefined
							: 'Confirmo que encendí el iPhone y lo conecté a Internet.'
					}
					message={
						releaseStates[releaseImei]?.teardown_status === 'erase_sent' ? (
							`Se comprobará si el iPhone ${releaseImei} terminó de liberarse. Manténgalo encendido y conectado a Internet.`
						) : (
							<div className="space-y-3">
								<p>
									Se borrará toda la información del iPhone {releaseImei}. Se eliminará su administración de Krece y este equipo no
									podrá ser financiado en Krece.
								</p>
								<p>Antes de confirmar, encienda el equipo y conéctelo a Internet.</p>
							</div>
						)
					}
					onClose={() => setReleaseImei(null)}
					onConfirm={async () => {
						const result = await handleRelease(releaseImei);
						if (result === true) setReleaseImei(null);
						return result;
					}}
				/>
			)}

			{/* Header */}
			<div className="mb-8">
				<h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Buscar iPhones</h1>
				<p className="mt-1 text-sm text-neutral-500">Verifica el estado de iPhones por IMEI</p>
			</div>

			{/* Search Input */}
			<div className="space-y-4">
				<Textarea
					floatingLabel="IMEIs"
					placeholder="Ingrese uno o más IMEIs separados por líneas o comas"
					value={imeiInput}
					onChange={setImeiInput}
					transformation={(value) => value.replace(/[^0-9\s,;\n]/g, '')}
					rows={4}
					containerClassName="w-full"
					disabled={loading}
				/>
				<Button
					variant="primary"
					onClick={handleSearch}
					disabled={loading || parsedIMEIs.length === 0}
					loading={loading}
				>
					<Search className="mr-2 h-4 w-4" />
					{parsedIMEIs.length > 0
						? `Buscar ${parsedIMEIs.length} ${parsedIMEIs.length === 1 ? 'dispositivo' : 'dispositivos'}`
						: 'Buscar'}
				</Button>
			</div>

			{/* Results Section */}
			{!loading && hasSearched && results.length > 0 && (
				<div className="mt-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
					{/* Summary Stats */}
					<div className="mb-8 flex items-center gap-6 text-sm">
						{enrolledCount > 0 && (
							<div className="flex items-center gap-2">
								<span className="h-2 w-2 rounded-full bg-green-500" />
								<span className="text-neutral-400">
									{enrolledCount} {enrolledCount === 1 ? 'listo' : 'listos'}
								</span>
							</div>
						)}
						{releasePendingCount > 0 && (
							<div className="flex items-center gap-2">
								<span className="h-2 w-2 rounded-full bg-yellow-500" />
								<span className="text-neutral-400">
									{releasePendingCount} en liberación
								</span>
							</div>
						)}
						{releasedCount > 0 && (
							<div className="flex items-center gap-2">
								<span className="h-2 w-2 rounded-full bg-green-500" />
								<span className="text-neutral-400">
									{releasedCount} {releasedCount === 1 ? 'liberado' : 'liberados'}
								</span>
							</div>
						)}
						{pendingCount > 0 && (
							<div className="flex items-center gap-2">
								<span className="h-2 w-2 rounded-full bg-yellow-500" />
								<span className="text-neutral-400">
									{pendingCount} {pendingCount === 1 ? 'pendiente' : 'pendientes'}
								</span>
							</div>
						)}
						{notFoundCount > 0 && (
							<div className="flex items-center gap-2">
								<span className="h-2 w-2 rounded-full bg-neutral-600" />
								<span className="text-neutral-400">
									{notFoundCount} no {notFoundCount === 1 ? 'encontrado' : 'encontrados'}
								</span>
							</div>
						)}
					</div>

					{/* Device List */}
					<div className="space-y-2">
						{sortedResults.map((device, idx) => {
							const status = getDeviceStatus(device);
							const imei = device.device_imei_no;
							const bypassState = imei ? bypassCodes[imei] : undefined;
							const releaseState = imei ? releaseStates[imei] : undefined;
							const canGetBypass = status.type === 'enrolled' || status.type === 'pending';
							const canRelease = status.type === 'enrolled' && releaseState?.teardown_status !== 'completed';
							return (
								<div
									key={idx}
									className="group overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50 transition-colors hover:border-neutral-700 hover:bg-neutral-900"
								>
									<div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
										<div className="flex min-w-0 items-center gap-3">
											{status.icon}
											<div>
												<code className="font-mono text-sm text-neutral-200">
													{imei || `Dispositivo ${idx + 1}`}
												</code>
												<p className="text-xs text-neutral-500">
													{releaseState?.teardown_status === 'completed'
														? 'Este iPhone fue liberado de Krece'
														: releaseState?.teardown_status === 'erase_sent'
															? 'La liberación de este iPhone está en proceso'
															: status.description}
													{status.type === 'pending' && status.daysLeft !== undefined && (
														<span className="ml-1 text-yellow-500/80">
															({status.daysLeft === 0
																? 'hoy'
																: status.daysLeft === 1
																	? '1 día'
																	: `${status.daysLeft} días`})
														</span>
													)}
												</p>
											</div>
										</div>
										<div className="flex flex-wrap items-center justify-end gap-2 self-end sm:self-auto">
											{canRelease && imei && (
												<Button
													variant={releaseState?.teardown_status === 'erase_sent' ? 'secondary' : 'destructive'}
													size="sm"
													className="!w-auto"
													Icon={<ShieldOff className="h-4 w-4" />}
													onClick={() => setReleaseImei(imei)}
												>
													{releaseState?.teardown_status === 'erase_sent' ? 'Verificar estado' : 'Liberar'}
												</Button>
											)}
											{canGetBypass && imei && !bypassState?.code && !releaseState?.bypass_code && (
												<button
													type="button"
													onClick={() => handleGetBypassCode(imei)}
													disabled={bypassState?.loading}
													className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300 disabled:opacity-50"
													title="Obtener código de bypass"
												>
													{bypassState?.loading ? (
														<Loader2 className="h-4 w-4 animate-spin" />
													) : (
														<KeyRound className="h-4 w-4" />
													)}
												</button>
											)}
											<span
												className={`rounded-full px-2.5 py-1 text-xs font-medium ${
													releaseState?.teardown_status === 'completed'
														? 'bg-green-500/10 text-green-400'
														: releaseState?.teardown_status === 'erase_sent'
															? 'bg-yellow-500/10 text-yellow-400'
															: status.type === 'enrolled'
																? 'bg-green-500/10 text-green-400'
																: status.type === 'pending'
																	? 'bg-yellow-500/10 text-yellow-400'
																	: status.type === 'not_found'
																		? 'bg-neutral-800 text-neutral-500'
																		: 'bg-orange-500/10 text-orange-400'
												}`}
											>
												{releaseState?.teardown_status === 'completed'
													? 'Liberado'
													: releaseState?.teardown_status === 'erase_sent'
														? 'Reseteo enviado'
														: status.label}
											</span>
										</div>
									</div>
									{releaseState && (
										<div className="space-y-3 border-t border-neutral-800 px-4 py-3">
											<div className="flex items-start gap-2">
												{releaseState.teardown_status === 'completed' ? (
													<CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
												) : (
													<Clock className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
												)}
												<p className="text-xs text-neutral-300">
													{releaseState.teardown_status === 'completed'
														? 'El iPhone ya no está administrado por Krece.'
														: 'El reseteo de fábrica fue enviado. Mantenga el iPhone encendido y conectado a internet hasta que se reinicie.'}
												</p>
											</div>
											{releaseState.bypass_code && (
												<div className="rounded-lg bg-neutral-950/50 p-3">
													<p className="mb-2 text-xs text-neutral-400">
														En la pantalla de bloqueo de activación, deje el usuario vacío e ingrese este código como contraseña.
													</p>
													<div className="flex items-center justify-between gap-3">
														<code className="break-all font-mono text-sm text-primary-400">{releaseState.bypass_code}</code>
														<button
															type="button"
															onClick={() => navigator.clipboard.writeText(releaseState.bypass_code!)}
															className="shrink-0 rounded p-1 text-neutral-500 transition-colors hover:text-neutral-300"
															title="Copiar código"
														>
															<Copy className="h-4 w-4" />
														</button>
													</div>
												</div>
											)}
										</div>
									)}
									{bypassState?.code && !releaseState && (
										<div className="flex items-center justify-between border-t border-neutral-800 px-4 py-2">
											<div className="flex items-center gap-2">
												<KeyRound className="h-3.5 w-3.5 text-primary-500" />
												<span className="text-xs text-neutral-400">Bypass:</span>
												<code className="font-mono text-sm text-primary-400">
													{bypassState.code}
												</code>
											</div>
											<button
												type="button"
												onClick={() => navigator.clipboard.writeText(bypassState.code!)}
												className="rounded p-1 text-neutral-500 transition-colors hover:text-neutral-300"
												title="Copiar código"
											>
												<Copy className="h-3.5 w-3.5" />
											</button>
										</div>
									)}
									{bypassState?.error && (
										<div className="border-t border-neutral-800 px-4 py-2">
											<p className="text-xs text-red-400">{bypassState.error}</p>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}

			{!loading && hasSearched && results.length === 0 && (
				<div className="mt-12 flex flex-col items-center justify-center text-center">
					<Search className="h-10 w-10 text-neutral-700" />
					<p className="mt-3 text-sm text-neutral-500">No se encontraron resultados</p>
				</div>
			)}
		</div>
	);
}
